import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { getDefaultStore, useAtom, useSetAtom } from "jotai";
import { useMemo, useState, type JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";
import type {
  RegulatoryDocumentLocationsQueryQuery,
  SolicitarDocumentoRegulatorioMutationMutation,
} from "@repo/graphql-types/graphql";
import { CpsToast } from "corpus";

import {
  type RegulatoryLocationOption,
  RegulatoryLocationSelect,
} from "@/components/regulatory-documents/regulatory-location-select";
import { Button } from "@/components/ui/button/button";
import { ProgressRoundedLayout } from "@/components/ui/layout/progress-rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import {
  CardSelectDescription,
  CardSelectRoot,
  CardSelectTitle,
} from "@/components/ui/select-input/card-select";
import { useGraphQL, useGraphQLMutationWithErrorHandler } from "@/hooks/use-graphql";
import { regulatoryDocumentAtom } from "@/lib/atoms/regulatory-document-atom";
import {
  type RegulatoryDocumentRequestResult,
  regulatoryDocumentResultAtom,
} from "@/lib/atoms/regulatory-document-result-atom";
import {
  isReasonStepComplete,
  type RegulatoryDocumentLocationDraft,
} from "@/lib/form-schemas/regulatory-document-schema";
import { dedupeLocationsByCep } from "@/lib/helpers/dedupe-locations-by-cep";
import { trackEvent } from "@/lib/tracking";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const RegulatoryDocumentLocationsQuery = graphql(/* GraphQL */ `
  query RegulatoryDocumentLocationsQuery($codTipoDocumentoRegulatorio: Int!) {
    unidades(
      where: { ativo: { _eq: true }, codUnidade: { _nin: [1, 6] } }
      order_by: { nomeLimpo: asc }
    ) {
      codUnidade
      nomeLimpo
      fotoUrl
      cep
      logradouro
      complemento
      complementoEnderecoRegulatorio
      bairro
      Cidade {
        nome
      }
    }
    tiposDocumentosRegulatorios(
      where: { codTipoDocumentoRegulatorio: { eq: $codTipoDocumentoRegulatorio } }
    ) {
      unidades {
        codUnidade
        disponivel
        motivoIndisponibilidade
      }
    }
  }
`);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const SolicitarDocumentoRegulatorioMutation = graphql(/* GraphQL */ `
  mutation SolicitarDocumentoRegulatorioMutation(
    $codTipoDocumentoRegulatorio: Int!
    $codMotivoDocumento: Int!
    $codUnidades: [Int!]
    $siglaUF: String
    $justificativa: String
  ) {
    solicitarDocumentoRegulatorio(
      input: {
        codTipoDocumentoRegulatorio: $codTipoDocumentoRegulatorio
        codMotivoDocumento: $codMotivoDocumento
        codUnidades: $codUnidades
        siglaUF: $siglaUF
        justificativa: $justificativa
      }
    ) {
      solicitacaoDocumentoRegulatorioOutput {
        codTipoDocumentoRegulatorio
        nome
        descricao
        arquivos {
          codUnidade
          nome
          url
        }
      }
      errors {
        __typename
        ... on ValidationError {
          message
        }
      }
    }
  }
`);

const toFileName = (...parts: string[]): string => {
  return parts
    .join(" ")
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-|-$/g, "");
};

type QueryLocation = RegulatoryDocumentLocationsQueryQuery["unidades"][number];

const mapSelectedLocations = (
  values: string[],
  locations: QueryLocation[],
): RegulatoryDocumentLocationDraft[] => {
  return values.flatMap((value) => {
    const location = locations.find((item) => String(item.codUnidade) === value);
    if (!location) return [];

    return [
      {
        codUnidade: location.codUnidade,
        nome: location.nomeLimpo ?? "",
        endereco: `${location.logradouro} - ${location.complemento} - ${location.bairro} - ${location.Cidade.nome}`,
      },
    ];
  });
};

type SolicitationOutput = NonNullable<
  SolicitarDocumentoRegulatorioMutationMutation["solicitarDocumentoRegulatorio"]["solicitacaoDocumentoRegulatorioOutput"]
>;

const buildRequestResults = (
  output: SolicitationOutput,
  selectedLocations: RegulatoryDocumentLocationDraft[],
): RegulatoryDocumentRequestResult[] => {
  return output.arquivos.flatMap((arquivo) => {
    if (!arquivo.url) return [];

    const selectedLocation = selectedLocations.find(
      (location) => location.codUnidade === arquivo.codUnidade,
    );

    return [
      {
        codUnidade: arquivo.codUnidade,
        nomeUnidade: arquivo.nome,
        enderecoUnidade: selectedLocation?.endereco ?? "",
        urlDownload: arquivo.url,
        nomeArquivo: toFileName(output.nome, arquivo.nome),
      },
    ];
  });
};

const RegulatoryDocumentLocationsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [draft, setDraft] = useAtom(regulatoryDocumentAtom);
  const setRequestResults = useSetAtom(regulatoryDocumentResultAtom);
  const [unavailableToast, setUnavailableToast] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isPending } = useGraphQL(RegulatoryDocumentLocationsQuery, {
    codTipoDocumentoRegulatorio: draft.codTipoDocumentoRegulatorio,
  });

  const { mutateAsync: createRequest } = useGraphQLMutationWithErrorHandler(
    SolicitarDocumentoRegulatorioMutation,
  );

  const dedupedLocations = useMemo(
    () => dedupeLocationsByCep(data?.unidades ?? []),
    [data],
  );

  const availableLocationIds = useMemo(() => {
    const documentType = data?.tiposDocumentosRegulatorios?.[0];
    return new Set(
      (documentType?.unidades ?? [])
        .filter((location) => location.disponivel)
        .map((location) => location.codUnidade),
    );
  }, [data]);

  const motivosPorUnidade = useMemo(() => {
    const documentType = data?.tiposDocumentosRegulatorios?.[0];
    return new Map(
      (documentType?.unidades ?? [])
        .filter((location) => Boolean(location.motivoIndisponibilidade))
        .map((location) => [location.codUnidade, location.motivoIndisponibilidade ?? ""]),
    );
  }, [data]);

  const locations: RegulatoryLocationOption[] = useMemo(() => {
    return dedupedLocations
      .map((location) => ({
        codUnidade: location.codUnidade,
        nome: location.nomeLimpo ?? "",
        fotoUrl: location.fotoUrl ?? "",
        unavailable: !availableLocationIds.has(location.codUnidade),
        motivoIndisponibilidade: motivosPorUnidade.get(location.codUnidade),
      }))
      .sort((a, b) => Number(a.unavailable) - Number(b.unavailable));
  }, [dedupedLocations, availableLocationIds, motivosPorUnidade]);

  const unavailableCount = locations.filter((location) => location.unavailable).length;
  const selectedLocationIds = useMemo(
    () => draft.selectedLocations.map((location) => String(location.codUnidade)),
    [draft.selectedLocations],
  );
  const selectedCount = draft.selectedLocations.length;

  const handleSelectionChange = (values: string[]): void => {
    const escolhidas = mapSelectedLocations(values, dedupedLocations);

    setDraft((prev) => ({
      ...prev,
      selectedLocations: escolhidas,
      temEntradasDiferentes: escolhidas.some((escolhida) =>
        dedupedLocations.some(
          (location) =>
            location.codUnidade === escolhida.codUnidade &&
            Boolean(location.complementoEnderecoRegulatorio?.trim()),
        ),
      ),
    }));
  };

  const handleContinue = async (): Promise<void> => {
    if (selectedCount === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await createRequest({
        codTipoDocumentoRegulatorio: draft.codTipoDocumentoRegulatorio,
        codUnidades: draft.selectedLocations.map((location) => location.codUnidade),
        codMotivoDocumento: draft.codMotivoDocumento,
        justificativa: draft.requiresJustification ? draft.justification.trim() : null,
      });

      const output =
        result.solicitarDocumentoRegulatorio.solicitacaoDocumentoRegulatorioOutput;
      if (!output) {
        throw new Error("Resposta vazia ao solicitar o documento");
      }

      const requests = buildRequestResults(output, draft.selectedLocations);
      setRequestResults(requests);

      trackEvent("Solicitação de Documento Regulatório Enviada", {
        codTipoDocumentoRegulatorio: draft.codTipoDocumentoRegulatorio,
        codMotivoDocumento: draft.codMotivoDocumento,
        quantidadeUnidades: requests.length,
        unidades: requests.map((request) => request.codUnidade),
      });

      void navigate({ to: "/settings/regulatory-documents/download", replace: true });
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ProgressRoundedLayout progress={2 / 3} className="pb-0">
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-3">
          <CardSelectRoot
            name="regulatory-document-locations"
            mode="multi"
            defaultValue={selectedLocationIds}
            onChange={handleSelectionChange}
          >
            <CardSelectTitle>
              De quais unidades você precisa do documento?
            </CardSelectTitle>
            <CardSelectDescription>Selecione uma ou mais unidades</CardSelectDescription>

            {isPending ? (
              <FullPageSpinner />
            ) : (
              <>
                <div className="flex items-center gap-3 pb-2">
                  <span className="h-px flex-1 bg-neutral-100" />
                  <span className="text-sm text-neutral-500">
                    {locations.length} unidades
                  </span>
                  <span className="h-px flex-1 bg-neutral-100" />
                </div>
                {unavailableCount > 0 ? (
                  <p className="pb-3 text-center text-xs text-neutral-400">
                    {unavailableCount === 1
                      ? "1 unidade ainda não tem este documento disponível"
                      : `${unavailableCount} unidades ainda não têm este documento disponível`}
                  </p>
                ) : null}
                <RegulatoryLocationSelect
                  locations={locations}
                  onUnavailableClick={(location) => {
                    setUnavailableToast(
                      location.motivoIndisponibilidade ??
                        `${location.nome} ainda não tem este documento disponível`,
                    );
                  }}
                />
              </>
            )}
          </CardSelectRoot>
        </div>

        <div className="sticky bottom-0 flex w-full flex-col bg-white pb-6 pt-4">
          <Button
            fullWidth
            onClick={() => void handleContinue()}
            disabled={selectedCount === 0}
            loading={isSubmitting}
          >
            {selectedCount > 1 ? `Continuar (${selectedCount} unidades)` : "Continuar"}
          </Button>
        </div>
      </div>

      <CpsToast
        title={unavailableToast}
        show={unavailableToast !== ""}
        onClose={() => {
          setUnavailableToast("");
        }}
        type="warning"
        duration={4}
      />
    </ProgressRoundedLayout>
  );
};

export const Route = createFileRoute("/settings/regulatory-documents/locations")({
  component: RegulatoryDocumentLocationsPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-documentos-regulatorios-no-app"]) {
      redirect({ to: "/settings", throw: true });
    }

    const draft = getDefaultStore().get(regulatoryDocumentAtom);
    if (!isReasonStepComplete(draft)) {
      redirect({ to: "/settings/regulatory-documents", throw: true });
    }
  },
});
